import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Award,
  BookOpen,
  CheckCircle2,
  Target,
  TrendingUp,
  Users,
  FileCheck,
  BarChart3,
  GraduationCap,
  Shield,
  Lightbulb,
  Building2,
  ChevronRight,
  Star,
  BookMarked,
  PenTool,
  Search
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const ResearchMethodology = () => {
  const navigate = useNavigate();

  const pageData = {
    title: 'Research Methodology Training Program',
    subtitle: 'Master Quantitative, Qualitative & Mixed Methods Research',
    description: 'Comprehensive training on research design, data collection, statistical analysis, publication strategies, research ethics, grant writing, and scientific communication for academics and researchers',
    meta_title: 'Research Methodology Training 2024 | Publication Strategies | Grant Writing | ISDE Academy',
    meta_description: 'Professional research methodology training covering quantitative, qualitative, mixed methods, SPSS, research ethics, publication in high-impact journals, grant proposal writing. Save 5+ hours weekly with modern research tools.',
    meta_keywords: 'research methodology training, quantitative research, qualitative research, mixed methods, publication strategy, research ethics, grant writing, scientific writing, data analysis',
    canonicalUrl: 'https://isde.academy/training/research-methodology'
  };

  const trainingModules = [
    {
      title: 'Research Design & Philosophy',
      icon: Lightbulb,
      duration: '5 Hours',
      topics: [
        'Research Paradigms: Positivism, Interpretivism, Pragmatism',
        'Research Questions and Hypothesis Formulation',
        'Literature Review and Gap Identification',
        'Research Design: Exploratory, Descriptive, Explanatory',
        'Experimental, Quasi-Experimental, and Non-Experimental Designs',
        'Longitudinal vs Cross-Sectional Studies',
        'Validity, Reliability, and Generalizability',
        'Ethical Considerations in Research Planning'
      ],
      outcomes: [
        'Strong foundation in research philosophy and design',
        'Ability to formulate research questions and hypotheses',
        'Selection of appropriate research design for objectives'
      ]
    },
    {
      title: 'Quantitative Research Methods',
      icon: BarChart3,
      duration: '6 Hours',
      topics: [
        'Sampling Techniques: Probability and Non-Probability',
        'Survey Design and Questionnaire Development',
        'Data Collection Methods and Instruments',
        'Descriptive Statistics: Measures of Central Tendency and Dispersion',
        'Inferential Statistics: t-tests, ANOVA, Regression',
        'Correlation and Causation Analysis',
        'SPSS and R for Statistical Analysis',
        'Data Interpretation and Presentation'
      ],
      outcomes: [
        'Expertise in quantitative research methodology',
        'Proficiency in statistical analysis tools',
        'Ability to interpret and present quantitative data'
      ]
    },
    {
      title: 'Qualitative Research Methods',
      icon: Search,
      duration: '5 Hours',
      topics: [
        'Qualitative Study Design and Planning',
        'Interview Techniques: Structured, Semi-Structured, Unstructured',
        'Focus Group Discussions and Facilitation',
        'Participant Observation and Ethnography',
        'Data Collection: Field Notes and Recording',
        'Thematic Analysis and Coding',
        'NVivo and ATLAS.ti for Qualitative Analysis',
        'Ensuring Credibility and Trustworthiness',
        'Ethical Standards in Qualitative Research'
      ],
      outcomes: [
        'Mastery of qualitative research techniques',
        'Expertise in thematic analysis and coding',
        'Skills in qualitative software tools'
      ]
    },
    {
      title: 'Mixed Methods Research',
      icon: Target,
      duration: '4 Hours',
      topics: [
        'Integration of Qualitative and Quantitative Methods',
        'Sequential Explanatory Design',
        'Sequential Exploratory Design',
        'Concurrent Triangulation Design',
        'Embedded and Transformative Designs',
        'Data Integration and Analysis Strategies',
        'NIH and Grant Proposal Requirements for Mixed Methods',
        'Reporting Mixed Methods Research'
      ],
      outcomes: [
        'Understanding of mixed methods research design',
        'Ability to integrate qualitative and quantitative data',
        'Grant proposal preparation for mixed methods studies'
      ]
    },
    {
      title: 'Publication Strategies & Scientific Writing',
      icon: PenTool,
      duration: '6 Hours',
      topics: [
        'Identifying Target Journals: Impact Factor, Scopus, Web of Science',
        'Understanding Journal Metrics: H-Index, CiteScore, SJR',
        'Structure of Research Papers: IMRAD Format',
        'Writing Compelling Abstracts and Titles',
        'Methods, Results, and Discussion Sections',
        'Citation Management: Mendeley, Zotero, EndNote',
        'Avoiding Plagiarism and Self-Plagiarism',
        'Responding to Peer Review Comments',
        'Open Access Publishing and Preprints',
        'Predatory Journals: Identification and Avoidance'
      ],
      outcomes: [
        'Expertise in academic writing and publication',
        'Strategic journal selection and submission',
        'Effective peer review response skills'
      ]
    },
    {
      title: 'Research Ethics & Grant Writing',
      icon: Shield,
      duration: '5 Hours',
      topics: [
        'Research Ethics Principles: Autonomy, Beneficence, Justice',
        'Informed Consent and Voluntary Participation',
        'Data Confidentiality and Privacy Protection',
        'Ethics Committee Review and Approval',
        'Research Misconduct: Fabrication, Falsification, Plagiarism',
        'Grant Proposal Structure and Components',
        'Identifying Funding Agencies: NIH, NSF, DST, ICMR',
        'Budget Preparation and Justification',
        'Writing Compelling Grant Narratives',
        'Post-Award Grant Management'
      ],
      outcomes: [
        'Complete understanding of research ethics',
        'Expertise in grant proposal development',
        'Ability to secure research funding'
      ]
    }
  ];

  const whyChoose = [
    {
      title: 'Practical Focus',
      description: 'Hands-on training with real datasets, analysis software, and publication exercises',
      icon: Lightbulb,
      color: 'emerald'
    },
    {
      title: 'Expert Faculty',
      description: 'Trainers with high-impact publications and successful grant acquisition',
      icon: Star,
      color: 'blue'
    },
    {
      title: 'Proven Results',
      description: '73% of participants report saving 5+ hours weekly with modern research tools',
      icon: TrendingUp,
      color: 'purple'
    },
    {
      title: 'Comprehensive',
      description: 'Complete coverage from research design to publication and grant funding',
      icon: BookMarked,
      color: 'orange'
    }
  ];

  const keyBenefits = [
    {
      title: 'For Researchers & Faculty',
      icon: GraduationCap,
      benefits: [
        'Enhanced research design and methodology skills',
        'Proficiency in quantitative and qualitative analysis',
        'Increased publication success in high-impact journals',
        'Grant proposal writing and funding acquisition',
        'Time-saving research tools and techniques (5+ hours/week)',
        'Career advancement through research excellence',
        'Network with research community'
      ]
    },
    {
      title: 'For Research Institutions',
      icon: Building2,
      benefits: [
        'Improved research output and quality',
        'Higher publication metrics and citations',
        'Increased external funding and grants',
        'Enhanced institutional reputation and rankings',
        'Systematic research ethics compliance',
        'Faculty development and capability building',
        'Research culture and innovation promotion'
      ]
    }
  ];

  const deliveryOptions = [
    {
      mode: 'On-Campus Workshop',
      duration: '4-5 Days Intensive',
      description: 'Comprehensive on-site training with hands-on data analysis, software tutorials, publication writing exercises, and grant proposal development workshops',
      ideal: 'Best for institutions building research capacity and publication culture'
    },
    {
      mode: 'Online Live Training',
      duration: '10-12 Sessions (4 weeks)',
      description: 'Interactive online sessions with recorded access, research templates, analysis software tutorials, and personalized publication feedback',
      ideal: 'Flexible option for individual researchers and distributed research teams'
    },
    {
      mode: 'Customized Mentoring',
      duration: 'Ongoing (3-6 months)',
      description: 'One-on-one research mentoring covering methodology design, data analysis, manuscript development, and grant proposal refinement with publication support',
      ideal: 'Personalized solution for researchers targeting high-impact publications'
    }
  ];

  const targetAudience = [
    'Research Scholars and PhD Students',
    'Faculty Members and Professors',
    'Postdoctoral Researchers',
    'Research Coordinators and Administrators',
    'Scientists and Industry Researchers',
    'Early Career Researchers',
    'Research Grant Coordinators'
  ];

  return (
    <>
      <Helmet>
        <title>{pageData.meta_title}</title>
        <meta name="description" content={pageData.meta_description} />
        <meta name="keywords" content={pageData.meta_keywords} />
        <link rel="canonical" href={pageData.canonicalUrl} />
        <meta property="og:title" content={pageData.meta_title} />
        <meta property="og:description" content={pageData.meta_description} />
        <meta property="og:url" content={pageData.canonicalUrl} />
        <meta name="twitter:title" content={pageData.meta_title} />
        <meta name="twitter:description" content={pageData.meta_description} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-700 text-white py-24 mt-20">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-2000"></div>
          </div>

          <div className="absolute top-10 right-10 opacity-20">
            <BookMarked className="h-32 w-32" />
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <Award className="h-5 w-5 text-yellow-200" />
                <span className="text-sm font-semibold">Research Excellence 2024</span>
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6">{pageData.title}</h1>
              <p className="text-xl md:text-2xl text-purple-100 mb-4">{pageData.subtitle}</p>
              <p className="text-lg text-purple-50 mb-8 max-w-3xl mx-auto">{pageData.description}</p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-purple-600 hover:bg-purple-50 px-8 py-6 text-lg font-semibold"
                >
                  Request Training
                </Button>
                <Button
                  onClick={() => navigate('/training-calendar')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-purple-600 px-8 py-6 text-lg font-semibold"
                >
                  View Schedule
                </Button>
              </div>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 120" fill="none">
              <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F9FAFB"/>
            </svg>
          </div>
        </section>

        {/* Training Modules */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Comprehensive Training Modules</h2>
          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Structured curriculum covering all aspects of research methodology from design to publication and funding
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {trainingModules.map((module, index) => {
              const IconComponent = module.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="bg-purple-50 p-3 rounded-lg">
                          <IconComponent className="h-6 w-6 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold mb-1">{module.title}</h3>
                          <p className="text-sm text-purple-600 font-semibold">Duration: {module.duration}</p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Topics:</h4>
                        <ul className="space-y-1.5">
                          {module.topics.map((topic, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                              <CheckCircle2 className="h-4 w-4 text-purple-500 flex-shrink-0 mt-0.5" />
                              <span>{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-purple-50 p-3 rounded-lg">
                        <h4 className="text-xs font-semibold text-purple-700 uppercase mb-1">Learning Outcomes:</h4>
                        <ul className="space-y-1">
                          {module.outcomes.map((outcome, i) => (
                            <li key={i} className="text-sm text-gray-700 flex items-start gap-1.5">
                              <span className="text-purple-600">•</span>
                              {outcome}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Why Choose Our Research Training?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyChoose.map((item, index) => {
                const IconComponent = item.icon;
                const colors = {
                  emerald: 'bg-emerald-50 text-emerald-600',
                  blue: 'bg-blue-50 text-blue-600',
                  purple: 'bg-purple-50 text-purple-600',
                  orange: 'bg-orange-50 text-orange-600'
                };

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full text-center hover:shadow-lg transition-all">
                      <CardContent className="p-6">
                        <div className={`${colors[item.color]} w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4`}>
                          <IconComponent className="h-6 w-6" />
                        </div>
                        <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Key Benefits</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {keyBenefits.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-purple-50 p-3 rounded-lg">
                          <IconComponent className="h-6 w-6 text-purple-600" />
                        </div>
                        <h3 className="text-xl font-bold">{item.title}</h3>
                      </div>
                      <ul className="space-y-2.5">
                        {item.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <ChevronRight className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Delivery Options */}
        <section className="bg-gradient-to-br from-gray-50 to-purple-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Training Delivery Options</h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              Choose the format that best suits your research goals and timeline
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              {deliveryOptions.map((option, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-xl transition-all hover:-translate-y-1">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold mb-2 text-purple-600">{option.mode}</h3>
                      <p className="text-sm font-semibold text-gray-700 mb-3">{option.duration}</p>
                      <p className="text-gray-600 mb-4">{option.description}</p>
                      <div className="bg-purple-50 p-3 rounded-lg">
                        <p className="text-xs font-semibold text-purple-700 uppercase mb-1">Ideal For:</p>
                        <p className="text-sm text-gray-700">{option.ideal}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Target Audience */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Who Should Attend?</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            This training is essential for all researchers, scholars, and faculty members pursuing research excellence
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {targetAudience.map((audience, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm border border-purple-100 hover:shadow-md transition-all"
              >
                <Users className="h-5 w-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{audience}</span>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600 text-white py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Ready to Elevate Your Research Impact?
              </h2>
              <p className="text-xl text-purple-50 mb-8">
                Join researchers saving 5+ hours weekly and publishing in high-impact journals with our comprehensive methodology training
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-purple-600 hover:bg-purple-50 px-8 py-6 text-lg font-semibold"
                >
                  Schedule Training
                </Button>
                <Button
                  onClick={() => navigate('/training/hei-training')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-purple-600 px-8 py-6 text-lg font-semibold"
                >
                  View All HEI Programs
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ResearchMethodology;
